<template>
    <section id="contact" class="contact">
        <div class="contact-grid">
            <div>
                <div class="contact-eyebrow">Get connected</div>
                <h2 class="contact-h2">Let's get you<br>online.</h2>
                <p class="contact-p">Tell us where you are and what you need. We will confirm what we can reach you on and what it costs.</p>
                <div class="engage">
                    <div v-for="(e, i) in steps" :key="e.step" class="engage-item">
                        <button
                            class="engage-btn ob-tap"
                            :aria-expanded="store.engageStep === i"
                            :aria-controls="`ob-engage-panel-${i}`"
                            @click="store.toggleEngageStep(i)"
                        >
                            <span class="engage-flex">
                                <span class="engage-main">
                                    <span class="engage-step">{{ e.step }}</span>
                                    <span class="engage-t">{{ e.t }}</span>
                                    <span class="engage-d">{{ e.d }}</span>
                                </span>
                                <span class="engage-chev" :class="{ open: store.engageStep === i }">⌄</span>
                            </span>
                        </button>
                        <div v-if="store.engageStep === i" :id="`ob-engage-panel-${i}`" role="region" class="engage-panel">{{ e.detail }}</div>
                    </div>
                </div>
            </div>

            <div class="form-card">
                <UForm :schema="schema" :state="form" class="form-grid" @submit="onSubmit">
                    <UFormField label="Your name" name="name">
                        <UInput v-model="form.name" placeholder="Full name" size="xl" class="w-full" />
                    </UFormField>
                    <UFormField label="Phone" name="phone">
                        <UInput v-model="form.phone" placeholder="+232 …" size="xl" class="w-full" />
                    </UFormField>
                    <UFormField label="Address or area" name="address" help="We will confirm what we can reach you on.">
                        <UInput v-model="form.address" placeholder="e.g. 12 Wilkinson Road, Freetown" size="xl" class="w-full" />
                    </UFormField>
                    <ObButton type="submit" variant="primary" size="lg" full-width>Request a callback</ObButton>
                    <a :href="COVERAGE_URL" target="_blank" rel="noopener" class="form-map-link">Or check the coverage map ↗</a>
                </UForm>
                <div class="contact-details">
                    <span>{{ ADDRESS }}</span>
                    <span class="contact-phone-row">
                        <a href="tel:3333" class="contact-phone">{{ PHONE_DISPLAY }}</a>
                        <button class="copy-btn ob-press" @click="copyNumber">{{ store.copied ? 'Copied' : 'Copy number' }}</button>
                    </span>
                    <a :href="`mailto:${EMAIL}`" class="contact-email">{{ EMAIL }}</a>
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import { computed, reactive } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { z } from 'zod'

    import ObButton from '@/components/ObButton.vue'
    import { ADDRESS, COVERAGE_URL, EMAIL, ENGAGE_CORP, ENGAGE_RETAIL, PHONE_COPY, PHONE_DISPLAY } from '@/data/content'
    import { PrintSuccess } from '@/libs/toast'
    import { useLandingStore } from '@/stores/landing'

    const store = useLandingStore()
    const { t } = useI18n()

    const steps = computed(() => store.isCorp ? ENGAGE_CORP : ENGAGE_RETAIL)

    const schema = z.object({
        name: z.string().min(2, t('validation.required')),
        phone: z.string().min(6, t('validation.phone')),
        address: z.string().min(3, t('validation.required')),
    })

    const form = reactive({ name: '', phone: '', address: '' })

    // TODO: wire to the real callback endpoint / CRM once the client provides one.
    const onSubmit = () => {
        PrintSuccess(t('success.request_sent'))
        form.name = ''
        form.phone = ''
        form.address = ''
    }

    const copyNumber = () => {
        try {
            if (navigator.clipboard) navigator.clipboard.writeText(PHONE_COPY)
        } catch { /* clipboard unavailable */ }
        store.markCopied()
    }
</script>

<style scoped>
    .contact { max-width: 1240px; margin: 0 auto; padding: 88px 44px; }
    .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 26px; align-items: stretch; }
    .contact-eyebrow { font-size: 12px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: var(--ob-blue-deep); }
    .contact-h2 {
        margin: 16px 0 0;
        font-size: clamp(32px, 4vw, 52px);
        font-weight: 800;
        letter-spacing: -.03em;
        line-height: 1;
        color: var(--ob-ink);
    }
    .contact-p { margin: 18px 0 30px; max-width: 440px; font-size: 17px; line-height: 1.6; color: var(--ob-ink-soft); }

    .engage { display: grid; gap: 0; border-top: 1px solid var(--ob-navy-a16); }
    .engage-item { border-bottom: 1px solid var(--ob-navy-a11); }
    .engage-btn { display: block; width: 100%; cursor: pointer; border: none; background: transparent; padding: 22px 14px 22px 0; font-family: 'Poppins', sans-serif; }
    .engage-flex { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
    .engage-main { text-align: left; }
    .engage-step { display: block; font-size: 11.5px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--ob-blue-deep); }
    .engage-t { display: block; margin-top: 8px; font-size: 16.5px; font-weight: 700; color: var(--ob-ink); }
    .engage-d { display: block; margin-top: 6px; font-size: 14.5px; line-height: 1.55; color: var(--ob-ink-sub); }
    .engage-chev { flex: none; font-size: 17px; color: var(--ob-blue-deep); transition: transform .25s; }
    .engage-chev.open { transform: rotate(180deg); }
    .engage-panel {
        margin: 0 14px 20px 0;
        padding: 14px 16px;
        border-radius: 12px;
        background: var(--ob-w-a80);
        border: 1px solid var(--ob-navy-a10);
        font-size: 13.5px;
        line-height: 1.6;
        color: var(--ob-ink);
    }

    /* DS Card, variant "elevated" */
    .form-card {
        background: var(--ob-white);
        border-radius: 16px;
        padding: 40px;
        box-shadow: 0 16px 40px rgba(0, 35, 70, .14);
    }
    .form-grid { display: grid; gap: 16px; }
    .form-map-link { text-align: center; font-size: 14px; font-weight: 600; color: var(--ob-blue-deep); padding-top: 4px; white-space: nowrap; text-decoration: none; }
    .contact-details {
        margin-top: 26px;
        padding-top: 22px;
        border-top: 1px solid var(--ob-navy-a10);
        display: grid;
        gap: 8px;
        font-size: 14px;
        color: var(--ob-ink-sub);
    }
    .contact-phone-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .contact-phone, .contact-email { color: var(--ob-ink); font-weight: 600; text-decoration: none; }
    .copy-btn {
        cursor: pointer;
        border: 1px solid var(--ob-blue-a35);
        background: var(--ob-white);
        color: var(--ob-blue-deep);
        font-family: 'Poppins', sans-serif;
        font-size: 12px;
        font-weight: 700;
        padding: 6px 12px;
        border-radius: 999px;
    }

    @media (max-width: 1024px) {
        .contact-grid { grid-template-columns: 1fr; gap: 40px; }
    }
    @media (max-width: 700px) {
        .contact { padding: 56px 20px; }
        .form-card { padding: 24px 20px; }
    }
</style>
